import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ListTodo, ChevronRight, Calendar, AlertTriangle, Plus, Tag, User, ExternalLink, RotateCcw } from 'lucide-react';
import { cn } from '../../lib/utils';

function getPriorityColor(priority) {
    if (priority === 'urgent') return 'bg-red-500';
    if (priority === 'high') return 'bg-orange-500';
    if (priority === 'medium') return 'bg-blue-500';
    return 'bg-slate-400';
}

const STATUS_LABEL = { todo: 'À faire', in_progress: 'À faire', postponed: 'Prolongée', done: 'Terminée' };

// Règle unifiée fév. 2026 : SOURCE DE VÉRITÉ = backend (task.is_overdue).
// Fallback local (cutoff = début du jour local) uniquement si le flag n'est pas fourni.
// Règle métier : une tâche N'EST PAS en retard le jour même — elle le devient le lendemain à 00:00.
function isTaskOverdue(dueDate, status, taskFlag) {
    if (typeof taskFlag === 'boolean') return taskFlag;
    if (!dueDate || status === 'done') return false;
    const due = new Date(dueDate);
    if (isNaN(due.getTime())) return false;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return due.getTime() < startOfToday.getTime();
}

function TaskWidgetItem({ task, onClick }) {
    const navigate = useNavigate();
    const isPostponed = task.status === 'postponed';
    // Une tâche prolongée se comporte comme une tâche normale : le retard s'évalue sur sa
    // NOUVELLE date d'échéance (la date de report). Ex. tâche reportée au 12 → en retard le 13.
    const isOverdue = isTaskOverdue(task.due_date, task.status, task.is_overdue);
    return (
        <div
            className={cn(
                "flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors",
                isOverdue && "border-red-200 bg-red-50/50",
                isPostponed && !isOverdue && "border-amber-200 bg-amber-50/40"
            )}
            onClick={onClick}
            data-testid={`task-widget-item-${task.id}`}
        >
            <div className={`w-2 h-2 rounded-full shrink-0 ${getPriorityColor(task.priority)}`} />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
                <div className="flex items-center gap-2 text-xs">
                    {isPostponed && (
                        <span className="text-amber-600 font-medium shrink-0" data-testid={`task-postponed-badge-${task.id}`}>
                            <RotateCcw className="w-3 h-3 inline mr-0.5" />Prolongée
                        </span>
                    )}
                    {task.prospect_id && (
                        // Le prospect_id existe → toujours cliquable, même si le nom manque.
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/prospects/${task.prospect_id}`); }}
                            className={cn(
                                "truncate hover:underline",
                                task.prospect_is_deleted ? "text-slate-500 hover:text-slate-700" : "text-blue-600 hover:text-blue-800"
                            )}
                            title={task.prospect_is_deleted ? "Ouvrir le dossier (en corbeille)" : "Ouvrir le dossier"}
                            data-testid={`task-widget-dossier-${task.id}`}
                        >
                            <Tag className="w-3 h-3 inline mr-0.5" />{task.prospect_name || 'Dossier lié'}
                        </button>
                    )}
                    {task.due_date && (
                        <span className={cn(isOverdue ? "text-red-600" : "text-slate-500")}>
                            <Calendar className="w-3 h-3 inline mr-0.5" />
                            {new Date(task.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            {isOverdue && " · en retard"}
                        </span>
                    )}
                </div>
            </div>
            {isOverdue && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
        </div>
    );
}

export function DashboardTasksWidget({ tasks = [] }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [selected, setSelected] = useState(null);

    // MES tâches (règle unifiée fév. 2026, alignée avec Uatu + backend) :
    // assigné à moi (assignee_ids ou assigned_to) OU tâche d'équipe où je suis membre.
    // Volontairement PAS `created_by` seul : si je crée sans m'assigner, ce n'est pas
    // « ma » tâche à faire (brief utilisateur).
    const myTasks = useMemo(() => {
        const uid = user?.id;
        return tasks.filter(t =>
            (t.assignee_ids || []).includes(uid)
            || t.assigned_to === uid
            || (t.is_team_task && (t.team_member_ids || []).includes(uid))
        );
    }, [tasks, user]);

    const stats = useMemo(() => ({
        todo: myTasks.filter(t => t.status === 'todo' || t.status === 'in_progress').length,
        postponed: myTasks.filter(t => t.status === 'postponed').length,
        done: myTasks.filter(t => t.status === 'done').length,
        // Une tâche prolongée past-due (au-delà de sa nouvelle date) est en retard, comme une tâche normale.
        overdue: myTasks.filter(t => isTaskOverdue(t.due_date, t.status, t.is_overdue)).length,
    }), [myTasks]);

    // Tâche prolongée = tâche normale dont la date d'échéance est la date de report → affichée
    // comme toute tâche active (todo / in_progress), triée par échéance.
    const todoTasks = myTasks.filter(t => t.status === 'todo' || t.status === 'in_progress' || t.status === 'postponed')
        .sort((a, b) => (a.due_date || '9999') < (b.due_date || '9999') ? -1 : 1);
    const displayTasks = todoTasks.slice(0, 5);

    const openProspect = (pid) => { if (pid) { setSelected(null); navigate(`/prospects/${pid}`); } };

    return (
        <Card data-testid="tasks-widget">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <ListTodo className="w-5 h-5" />
                        Mes tâches
                        {stats.overdue > 0 && (
                            <span className="flex items-center gap-1 text-xs font-normal text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                <AlertTriangle className="w-3 h-3" />{stats.overdue} en retard
                            </span>
                        )}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')} data-testid="tasks-widget-see-all">
                        Voir tout <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {displayTasks.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                        <ListTodo className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">Aucune tâche en attente</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/tasks')}>
                            <Plus className="w-4 h-4 mr-1" />Créer une tâche
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {displayTasks.map(task => (
                            <TaskWidgetItem key={task.id} task={task} onClick={() => setSelected(task)} />
                        ))}
                        {todoTasks.length > 5 && (
                            <Button variant="ghost" size="sm" className="w-full text-slate-500 hover:text-slate-700" onClick={() => navigate('/tasks')}>
                                + {todoTasks.length - 5} autres tâches
                            </Button>
                        )}
                    </div>
                )}
                <div className="flex items-center justify-between mt-4 pt-3 border-t text-xs text-slate-500">
                    <span>{stats.todo} à faire</span>
                    <span className="text-amber-600">{stats.postponed} prolongées</span>
                    <span className="text-green-600">{stats.done} terminées</span>
                </div>
            </CardContent>

            {/* Détail d'une tâche : identifier la tâche + ouvrir le prospect lié */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-md" data-testid="task-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader><DialogTitle className="pr-6">{selected.title}</DialogTitle></DialogHeader>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <Badge className={cn('text-white', getPriorityColor(selected.priority))}>{selected.priority}</Badge>
                                    <Badge variant="outline">{STATUS_LABEL[selected.status] || selected.status}</Badge>
                                    {selected.module && <span className="text-xs text-slate-400">{selected.module === 'both' ? 'Tout le dossier' : (selected.module === 'chantier' ? 'Travaux d’optimisation' : 'Courtage')}</span>}
                                </div>
                                {selected.description && <p className="text-slate-600">{selected.description}</p>}
                                {selected.due_date && (
                                    <p className="flex items-center gap-1 text-slate-600"><Calendar className="w-4 h-4" />Échéance : {new Date(selected.due_date).toLocaleDateString('fr-FR')}</p>
                                )}
                                {(selected.assignee_names?.length || selected.assigned_to_name) && (
                                    <p className="flex items-center gap-1 text-slate-600"><User className="w-4 h-4" />{(selected.assignee_names || [selected.assigned_to_name]).filter(Boolean).join(', ')}</p>
                                )}
                                {selected.prospect_id ? (
                                    <Button className="w-full mt-2" onClick={() => openProspect(selected.prospect_id)} data-testid="task-detail-open-prospect">
                                        <ExternalLink className="w-4 h-4 mr-2" />Ouvrir le dossier {selected.prospect_name || ''}
                                    </Button>
                                ) : (
                                    <p className="text-xs text-slate-400">Aucun dossier lié</p>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    );
}
